import React, { useState, useEffect, useMemo } from 'react';
import { formatISO, parseISO, format } from 'date-fns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Autocomplete,
  Avatar,
  Box,
  Alert,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Typography
} from '@mui/material';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import AdminButton from 'components/common/AdminButton';
import { API, graphqlOperation } from 'aws-amplify';
import { managerService, roleService } from '../services/domain';
import { 
  createOrgUnit, 
  createOrgUnitEmployee, 
  createHierarchyChangeLog 
} from 'graphql/mutations';
import { 
  employeesByClient, 
  orgUnitsByClientAndLevel 
} from 'graphql/queries';
import { useUser } from 'components/RDAuthContext';
import { v4 as uuidv4 } from 'uuid';

function CreateOrgUnitModal({ open, onClose, onSuccess, hierarchyLevels }) {
  const { user } = useUser();
  const [formData, setFormData] = useState({
    name: '',
    orgLevel: '',
    parentOrgUnitId: null,
    isActive: true,
    changeReason: ''
  });
  const [availableManagers, setAvailableManagers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [managerAssignments, setManagerAssignments] = useState([]);
  const [availableParentUnits, setAvailableParentUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      // Filter available org levels (only show levels that are actually in use)
      const availableLevels = hierarchyLevels.filter(level => level.isUsed !== false);
      
      // If only one level is available, auto-set it
      const defaultOrgLevel = availableLevels.length === 1 ? availableLevels[0].levelId : '';
      
      // Reset form to initial state
      setFormData({
        name: '',
        orgLevel: defaultOrgLevel,
        parentOrgUnitId: null,
        isActive: true,
        changeReason: ''
      });
      // Start with one empty assignment row
      setManagerAssignments([{ employee: null, role: null, isExisting: false }]);
      setError(null);
    }
  }, [open, hierarchyLevels]);

  // Fetch available managers (employees) and roles
  useEffect(() => {
    const fetchAvailableData = async () => {
      if (!user?.client || !open || !formData.orgLevel) return;
      
      try {
        setLoading(true);
        
        // Fetch both employees and roles in parallel
        const [managers, roles] = await Promise.all([
          managerService.getAvailableManagers(user.client),
          roleService.getOrgUnitRoles(user.client, formData.orgLevel)
        ]);
        
        setAvailableManagers(managers);
        setAvailableRoles(roles);
      } catch (error) {
        console.error('Error fetching available data:', error);
        setError('Failed to load available managers or roles');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAvailableData();
  }, [user, open, formData.orgLevel]);

  // Fetch available parent units based on org level
  useEffect(() => {
    const fetchParentUnits = async () => {
      if (!user?.client || !open || !formData.orgLevel) return;
      
      // Determine the parent level based on current level
      const currentLevelNum = parseInt(formData.orgLevel.replace('ORGLEVEL_', ''));
      if (currentLevelNum >= 3) {
        // Level 3 is the highest, no parent units
        setAvailableParentUnits([]);
        return;
      }
      
      const parentLevel = `ORGLEVEL_${currentLevelNum + 1}`;
      
      try {
        const unitsResult = await API.graphql(
          graphqlOperation(orgUnitsByClientAndLevel, {
            clientId: user.client,
            orgLevel: { eq: parentLevel }
          })
        );
        
        const units = unitsResult?.data?.orgUnitsByClientAndLevel?.items || [];
        const parentUnits = units
          .filter(u => u.isActive !== false) // Only show active parent units
          .map(u => ({
            id: u.id,
            name: u.name,
            orgLevel: u.orgLevel
          }));
        
        setAvailableParentUnits(parentUnits);
      } catch (error) {
        console.error('Error fetching parent units:', error);
      }
    };
    
    fetchParentUnits();
  }, [user, open, formData.orgLevel]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmployeeChange = (index, newValue) => {
    const updated = [...managerAssignments];
    updated[index] = { ...updated[index], employee: newValue };
    setManagerAssignments(updated);
  };

  const handleRoleChange = (index, newValue) => {
    const updated = [...managerAssignments];
    updated[index] = { ...updated[index], role: newValue };
    setManagerAssignments(updated);
  };

  const handleAddAssignment = () => {
    setManagerAssignments([...managerAssignments, { employee: null, role: null, isExisting: false }]);
  };

  const handleRemoveAssignment = (index) => {
    setManagerAssignments(managerAssignments.filter((_, i) => i !== index));
  };

  const handleParentUnitChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      parentOrgUnitId: newValue?.id || null
    }));
  };

  const handleSave = async () => {
    // Validation
    if (!formData.name) {
      setError('Unit name is required');
      return;
    }
    
    if (!formData.orgLevel) {
      setError('Organizational level is required');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      // Generate a unique ID for the new org unit using UUID v4
      const newUnitId = uuidv4();
      
      // Create the new org unit (only include fields that exist in the schema)
      const createInput = {
        id: newUnitId,
        clientId: user.client,
        name: formData.name,
        orgLevel: formData.orgLevel,
        parentOrgUnitId: formData.parentOrgUnitId,
        isActive: formData.isActive
      };

      const createResult = await API.graphql(
        graphqlOperation(createOrgUnit, { input: createInput })
      );
      
      const createdUnit = createResult?.data?.createOrgUnit;

      // Create manager assignments
      const validAssignments = managerAssignments.filter(a => a.employee && a.role);
      if (validAssignments.length > 0) {
        const now = formatISO(new Date());
        
        const createPromises = validAssignments.map(async (assignment) => {
          const newAssignment = {
            clientId: user.client,
            employeeId: assignment.employee.id, // This is the Employee.id
            orgUnitId: newUnitId,
            managementLevel: formData.orgLevel,
            roleDefinitionId: assignment.role.id,
            isActive: true,
            effectiveDate: now,
            assignedBy: user.id,
            assignmentReason: formData.changeReason || 'Initial assignment via Location Admin',
            employeeNameSnapshot: `${assignment.employee.firstName} ${assignment.employee.lastName}`.trim() || 'Unknown',
            orgUnitNameSnapshot: formData.name,
            roleSnapshot: assignment.role.roleName,
            createdAt: now,
            lastModified: now,
            changedBy: user.id
          };

          try {
            await API.graphql(
              graphqlOperation(createOrgUnitEmployee, { input: newAssignment })
            );
          } catch (error) {
            console.error('Error creating manager assignment:', error);
            // Don't throw here - the org unit was created successfully, just log the error
            console.error('Manager assignment failed but org unit was created');
          }
        });
        
        await Promise.all(createPromises);
      }
      
      // Create HierarchyChangeLog entry
      const fieldChanges = [
        {
          fieldName: 'created',
          oldValue: null,
          oldDisplayValue: 'N/A',
          newValue: newUnitId,
          newDisplayValue: formData.name
        }
      ];
      
      // Add manager assignments to field changes if managers were selected
      if (validAssignments.length > 0) {
        const managerNames = validAssignments.map(a => 
          `${a.employee?.firstName} ${a.employee?.lastName}`.trim() || 'Unknown'
        ).join(', ');
        fieldChanges.push({
          fieldName: 'managers',
          oldValue: '',  // No previous managers (new unit)
          oldDisplayValue: 'None',
          newValue: validAssignments.map(a => a.employee?.id).join(','),  // Store the IDs
          newDisplayValue: managerNames  // Display the names
        });
      }
      
      const changeLog = {
        clientId: user.client,
        changeType: validAssignments.length > 0 ? 'OrgUnit,ManagerAssignment' : 'OrgUnit',
        changeSummary: `New OrgUnit Created: ${formData.name}`,
        changeDetails: [{
          entityType: 'OrgUnit',
          entityId: newUnitId,
          entityName: formData.name,
          operationType: 'CREATE',
          fieldChanges: fieldChanges,
          metadata: JSON.stringify({
            orgLevel: formData.orgLevel,
            parentOrgUnitId: formData.parentOrgUnitId,
            managerCount: validAssignments.length,
            createdVia: 'Location Admin'
          })
        }],
        affectedLocations: [],
        affectedOrgUnits: [newUnitId],
        changedBy: user.id,
        changedByName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || user.userName,
        changedAt: formatISO(new Date()),
        changeReason: formData.changeReason || 'New organizational unit created via Location Admin interface'
      };

      const changeLogResult = await API.graphql(
        graphqlOperation(createHierarchyChangeLog, { input: changeLog })
      );

      // Success
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error creating org unit:', error);
      setError('Failed to create organizational unit. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Find selected parent unit using useMemo for consistency
  const selectedParentUnit = useMemo(() => {
    if (!formData.parentOrgUnitId || availableParentUnits.length === 0) {
      return null;
    }
    return availableParentUnits.find(u => String(u.id) === String(formData.parentOrgUnitId)) || null;
  }, [formData.parentOrgUnitId, availableParentUnits]);

  // Filter available org levels (only show levels that are actually in use)
  const availableLevels = hierarchyLevels.filter(level => level.isUsed !== false);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
        }
      }}
    >
      <DialogTitle>
        Create New Organizational Unit
      </DialogTitle>
      
      <DialogContent>
        <Box pt={2}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          {/* Unit Name */}
          <TextField
            fullWidth
            label="Unit Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            margin="normal"
            required
            variant="outlined"
            autoFocus
          />
          
          {/* Org Level Selection */}
          <Autocomplete
            options={availableLevels}
            value={availableLevels.find(level => level.levelId === formData.orgLevel) || null}
            onChange={(event, newValue) => handleInputChange('orgLevel', newValue?.levelId || '')}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.levelId === value.levelId}
            disabled={availableLevels.length === 1}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Organizational Level"
                margin="normal"
                variant="outlined"
                required
                helperText={availableLevels.length === 1 ? "Only one organizational level is configured" : ""}
              />
            )}
            disableClearable
          />
          
          {/* Parent Unit (shown when applicable) */}
          {formData.orgLevel && availableParentUnits.length > 0 && (
            <Autocomplete
              options={availableParentUnits}
              value={selectedParentUnit}
              onChange={handleParentUnitChange}
              getOptionLabel={(option) => option.name}
              getOptionKey={(option) => option.id}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parent Unit (Optional)"
                  margin="normal"
                  variant="outlined"
                  helperText="Select the parent unit in the hierarchy"
                />
              )}
              loading={loading}
              loadingText="Loading parent units..."
              noOptionsText="No parent units available"
            />
          )}
          
          {/* Manager Assignments Section */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="600">
                Manager Assignments (Optional)
              </Typography>
              <AdminButton
                variant="outlined"
                onClick={handleAddAssignment}
                disabled={saving}
                startIcon={<AddCircleOutlineIcon />}
                size="small"
              >
                Add Assignment
              </AdminButton>
            </Box>

            {/* Assignment Rows */}
            {managerAssignments.map((assignment, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 2, mb: 1 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={5}>
                    <Autocomplete
                      value={assignment.employee}
                      options={availableManagers.filter(u => 
                        !managerAssignments.some((a, i) => i !== index && a.employee?.id === u.id)
                      )}
                      getOptionLabel={(option) => 
                        `${option.firstName} ${option.lastName}`
                      }
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                            {option.firstName?.charAt(0) || 'E'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2">
                              {option.firstName} {option.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.email}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Employee"
                          placeholder="Select employee..."
                          variant="outlined"
                          size="small"
                        />
                      )}
                      onChange={(event, newValue) => handleEmployeeChange(index, newValue)}
                      loading={loading}
                      disabled={saving}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <Autocomplete
                      value={assignment.role}
                      options={availableRoles}
                      getOptionLabel={(option) => option.roleName || ''}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Role"
                          placeholder="Select role..."
                          variant="outlined"
                          size="small"
                        />
                      )}
                      onChange={(event, newValue) => handleRoleChange(index, newValue)}
                      loading={loading}
                      disabled={saving || !formData.orgLevel}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <IconButton 
                        onClick={() => handleRemoveAssignment(index)}
                        disabled={saving}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            ))}

            {managerAssignments.length === 0 && (
              <Box 
                sx={{ 
                  p: 3, 
                  textAlign: 'center', 
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: 'action.hover'
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No managers assigned. Click "Add Assignment" to assign managers.
                </Typography>
              </Box>
            )}
          </Box>
          
          {/* Active Status */}
          <Box mt={2} mb={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  color="success"
                />
              }
              label={
                <Typography variant="body2">
                  {formData.isActive ? 'Active' : 'Inactive'}
                </Typography>
              }
            />
          </Box>
          
          {!formData.isActive && (
            <Alert severity="info" sx={{ mt: 1 }}>
              This unit will be created as inactive and will not appear in the active hierarchy.
            </Alert>
          )}
          
          {/* Change Reason */}
          <TextField
            fullWidth
            label="Reason for Creation"
            value={formData.changeReason}
            onChange={(e) => handleInputChange('changeReason', e.target.value)}
            margin="normal"
            variant="outlined"
            multiline
            rows={3}
            inputProps={{ maxLength: 500 }}
            helperText={`Optional: Document why this unit is being created (${formData.changeReason.length}/500 characters)`}
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <AdminButton
          onClick={onClose}
          variant="outlined"
          disabled={saving}
        >
          Cancel
        </AdminButton>
        <AdminButton
          onClick={handleSave}
          variant="contained"
          disabled={
            saving ||
            !formData.name ||
            !formData.orgLevel ||
            // Check for incomplete assignments (one field filled but not the other)
            managerAssignments.some(a => (a.employee && !a.role) || (!a.employee && a.role))
          }
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Create Unit'}
        </AdminButton>
      </DialogActions>
    </Dialog>
  );
}

export default CreateOrgUnitModal;